import { lazy, Suspense, useState, useCallback } from "react";
import { Switch, Route } from "wouter";
import { queryClient } from "./lib/queryClient";
import { QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import StartupPage from "@/components/StartupPage";
import CookieBanner from "@/components/CookieBanner";

const Landing = lazy(() => import("@/pages/landing"));
const Services = lazy(() => import("@/pages/services"));
const ServiceDetails = lazy(() => import("@/pages/services/service-details"));
const About = lazy(() => import("@/pages/about"));
const Contact = lazy(() => import("@/pages/contact"));
const FAQ = lazy(() => import("@/pages/faq"));
const Gallery = lazy(() => import("@/pages/gallery"));
const Blog = lazy(() => import("@/pages/blog"));
const BlogPost = lazy(() => import("@/pages/blog/post-details"));
const LocationsHub = lazy(() => import("@/pages/locations"));
const LocationDetails = lazy(() => import("@/pages/locations/location-details"));
const Coverage = lazy(() => import("@/pages/coverage"));
const Social = lazy(() => import("@/pages/social"));
const BookingWizard = lazy(() => import("@/pages/booking"));
const EOTBooking = lazy(() => import("@/pages/booking/eot"));
const CarpetBooking = lazy(() => import("@/pages/booking/carpet"));
const BookingSuccess = lazy(() => import("@/pages/booking/success"));
const BookingCancelled = lazy(() => import("@/pages/booking/cancelled"));
const BookingDetails = lazy(() => import("@/pages/booking/[id]"));
const Login = lazy(() => import("@/pages/login"));
const CustomerDashboard = lazy(() => import("@/pages/customer/dashboard"));
const AdminDashboard = lazy(() => import("@/pages/admin/dashboard"));
const Privacy = lazy(() => import("@/pages/privacy"));
const Cookies = lazy(() => import("@/pages/cookies"));
const Terms = lazy(() => import("@/pages/terms"));
const NotFound = lazy(() => import("@/pages/not-found"));

function PageLoader() {
  return (
    <div className="min-h-screen flex items-center justify-center text-slate-600">
      <span role="status" aria-live="polite">
        Loading...
      </span>
    </div>
  );
}

function Router() {
  return (
    <Suspense fallback={<PageLoader />}>
      <Switch>
        {/* Public routes available to everyone */}
        <Route path="/" component={Landing} />
        <Route path="/services" component={Services} />
        <Route path="/services/:slug" component={ServiceDetails} />
        <Route path="/about" component={About} />
        <Route path="/contact" component={Contact} />
        <Route path="/faq" component={FAQ} />
        <Route path="/gallery" component={Gallery} />
        <Route path="/blog" component={Blog} />
        <Route path="/blog/:slug" component={BlogPost} />
        <Route path="/locations" component={LocationsHub} />
        <Route path="/locations/:slug" component={LocationDetails} />
        <Route path="/coverage" component={Coverage} />
        <Route path="/social" component={Social} />
        <Route path="/privacy" component={Privacy} />
        <Route path="/cookies" component={Cookies} />
        <Route path="/terms" component={Terms} />
        {/* <Route path="/booking" component={BookingWizard} /> */}
        <Route path="/booking"><BookingWizard /></Route>
        <Route path="/booking/eot" component={EOTBooking} />
        <Route path="/booking/carpet" component={CarpetBooking} />
        <Route path="/booking/:id" component={BookingDetails} />
        <Route path="/booking/success" component={BookingSuccess} />
        <Route path="/booking/cancelled" component={BookingCancelled} />
        <Route path="/login" component={Login} />
        <Route path="/dashboard" component={CustomerDashboard} />
        <Route path="/admin" component={AdminDashboard} />
        <Route component={NotFound} />
      </Switch>
    </Suspense>
  );
}

function App() {
  const [showStartup, setShowStartup] = useState(true);

  const handleStartupComplete = useCallback(() => {
    setShowStartup(false);
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <CookieBanner />
        <Router />
        {showStartup && (
          <StartupPage onComplete={handleStartupComplete} />
        )}
      </TooltipProvider>
    </QueryClientProvider>
  );
}

export default App;
